import { ref, computed } from '@vue/composition-api'
import { apiToastSuccess } from '@/@core/utils/toast'
import analytics from '@/@core/utils/analytics'
import constants, { retailPrices, wholesalePrices } from '@/constants'
import store from '@/store'
import { UPDATE_ENTITY, FETCH_PROFILE } from '@/store/modules/auth.module'

export function useSegmentation() {
  const loading = ref(false)
  const segmentationLocal = ref(null)
  const allCountriesLocal = ref([])
  const kingpinPresenceCountriesLocal = ref([])
  const selectAllCountries = ref(false)

  // Keep references to the raw lists for filtering
  const countriesRef = ref([])
  const kingpinPresenceCountriesRef = ref([])

  const profileData = computed(() => store.getters.profile)
  const isNotEntityAdmin = computed(() => !store.getters.isEntityAdmin)
  const isBrand = computed(() => store.getters.isBrand)

  const countryOptionGroups = computed(() => [
    {
      title: 'Kingpin Presence',
      options: kingpinPresenceCountriesLocal.value,
    },
    {
      title: 'All Countries',
      options: allCountriesLocal.value,
    },
  ])

  function normalizePriceField(value) {
    if (value && typeof value === 'object') {
      return value.value || null
    }
    if (value === '[object Object]') {
      return null
    }
    return value
  }

  function buildDefault(segData) {
    let data = segData ? JSON.parse(JSON.stringify(segData)) : null
    if (!data) {
      data = {
        avgWholesalePrice: null,
        avgRetailPrice: null,
        minimumOrderValue: null,
        departments: [],
        restrictedCountries: [],
      }
    }
    if (!data.departments) {
      data.departments = []
    }
    if (!data.restrictedCountries) {
      data.restrictedCountries = []
    }
    data.avgRetailPrice = normalizePriceField(data.avgRetailPrice)
    data.avgWholesalePrice = normalizePriceField(data.avgWholesalePrice)
    return data
  }

  function initSegmentation(segData, countries, kingpinPresenceCountries) {
    countriesRef.value = countries || []
    kingpinPresenceCountriesRef.value = kingpinPresenceCountries || []
    segmentationLocal.value = buildDefault(segData)
    setCountries()
  }

  function resetForm(segData) {
    segmentationLocal.value = buildDefault(segData)
  }

  function setCountries(searchingText = '') {
    if (searchingText) {
      const q = searchingText.toLowerCase()
      allCountriesLocal.value = countriesRef.value.filter(
        country => country.toLowerCase().indexOf(q) !== -1,
      )
      kingpinPresenceCountriesLocal.value = kingpinPresenceCountriesRef.value.filter(
        country => country.toLowerCase().indexOf(q) !== -1,
      )
    } else {
      allCountriesLocal.value = countriesRef.value
      kingpinPresenceCountriesLocal.value = kingpinPresenceCountriesRef.value
      isAllCountrySelected()
    }
  }

  function handleSelectCountry(countries) {
    segmentationLocal.value.restrictedCountries = countries
    isAllCountrySelected()
  }

  function handleSelectAllCountries() {
    if (selectAllCountries.value) {
      segmentationLocal.value.restrictedCountries = [...allCountriesLocal.value]
    } else {
      segmentationLocal.value.restrictedCountries = []
    }
  }

  function isAllCountrySelected(value) {
    if (value) {
      segmentationLocal.value.restrictedCountries = value
    }
    selectAllCountries.value = segmentationLocal.value.restrictedCountries.length === countriesRef.value.length
  }

  async function saveSegmentation(onSuccess) {
    segmentationLocal.value.minimumOrderValue = parseInt(
      segmentationLocal.value.minimumOrderValue,
      10,
    )
    loading.value = true
    const payload = {
      departments: segmentationLocal.value.departments,
      restrictedCountries: segmentationLocal.value.restrictedCountries,
      avgRetailPrice: segmentationLocal.value.avgRetailPrice,
      avgWholesalePrice: segmentationLocal.value.avgWholesalePrice,
      minimumOrderValue: segmentationLocal.value.minimumOrderValue,
    }
    return store.dispatch(UPDATE_ENTITY, payload)
      .then(res => {
        const message = res?.data?.message || 'Profile updated successfully.'
        apiToastSuccess(message)
        if (onSuccess) {
          onSuccess(segmentationLocal.value)
        }
        analytics.track(constants.TRACKS.ACTIONS.SAVE_PROFILE_SETTINGS_SEGMENTATION)
        return store.dispatch(FETCH_PROFILE)
      })
      .catch(() => { })
      .finally(() => {
        loading.value = false
      })
  }

  return {
    loading,
    segmentationLocal,
    allCountriesLocal,
    kingpinPresenceCountriesLocal,
    selectAllCountries,
    profileData,
    isNotEntityAdmin,
    isBrand,
    countryOptionGroups,
    retailPrices,
    wholesalePrices,
    initSegmentation,
    buildDefault,
    resetForm,
    setCountries,
    handleSelectCountry,
    handleSelectAllCountries,
    isAllCountrySelected,
    saveSegmentation,
  }
}
